import { useState, useEffect, useCallback, createContext, useContext } from "react";

export interface UserSettings {
  // Electrical
  conductorMaterial: "cu" | "al";
  voltage: string;
  phases: "1" | "3";
  unitSystem: "imperial" | "metric";
  wireInsulation: "thhn" | "thwn" | "xhhw";
  racewayType: "emt" | "imc" | "rgs" | "pvc40" | "pvc80";
  // Piping
  pipeMaterial: "0" | "1" | "2" | "3" | "4"; // index into PIPE_MATERIALS_HW
  fittingAngle: "22.5" | "30" | "45" | "60";
  drainSlope: "1/16" | "1/8" | "1/4" | "1/2";
  pipeVelocityPreset: "0" | "1" | "2" | "3"; // index into VELOCITY_PRESETS
  // Ductwork
  ductVelocity: "400" | "600" | "800" | "1200" | "1500" | "2500";
  ductMaterial: "galvanized" | "aluminum" | "flex" | "fiberglass";
  sealClass: "unsealed" | "C" | "B" | "A";
}

const DEFAULTS: UserSettings = {
  conductorMaterial: "cu",
  voltage: "480",
  phases: "3",
  unitSystem: "imperial",
  wireInsulation: "thhn",
  racewayType: "emt",
  pipeMaterial: "0",
  fittingAngle: "45",
  drainSlope: "1/4",
  pipeVelocityPreset: "0",
  ductVelocity: "600",
  ductMaterial: "galvanized",
  sealClass: "unsealed",
};

const STORAGE_KEY = "trades-toolbelt-settings";

export const readSettings = (): UserSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
};

export const useSettings = () => {
  const [settings, setSettingsState] = useState<UserSettings>(readSettings);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const updateSetting = useCallback(<K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettingsState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetDefaults = useCallback(() => {
    setSettingsState(DEFAULTS);
  }, []);

  return { settings, updateSetting, resetDefaults, DEFAULTS };
};
